import { useController } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/Components/shadcn/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/shadcn/select';
import { cn } from '@/lib/utils';
import { DropdownOption } from '@talentmesh/utils';

export type RHFSelectOption = { id: string; name: string };

export type RHFSelectProps = {
    name: string;
    label: string;
    options: RHFSelectOption[];
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
    fieldDataCy?: string;
    placeholder?: string;
};

export const mapDropdownOptionToRHFSelectOption = ({ id, name }: DropdownOption): RHFSelectOption => ({ id, name });

function RHFSelect({
    name,
    label,
    options,
    disabled,
    className,
    labelClassName,
    fieldDataCy,
    placeholder,
}: RHFSelectProps) {
    const { field, fieldState } = useController({ name });
    const error = fieldState.error;
    const raw = field.value != null && field.value !== '' ? String(field.value) : '';
    const selectValue = raw === '' ? undefined : raw;

    return (
        <Field className={cn('w-full')} data-invalid={error ? true : undefined} data-cy={fieldDataCy}>
            <FieldLabel className={labelClassName}>{label}</FieldLabel>
            <Select
                value={selectValue}
                onValueChange={field.onChange}
                disabled={disabled}
                onOpenChange={(open) => {
                    if (!open && raw) {
                        field.onBlur();
                    }
                }}
            >
                <SelectTrigger
                    className={cn(
                        'w-full',
                        'aria-invalid:border-destructive aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive dark:aria-invalid:focus-visible:border-destructive/50 dark:aria-invalid:focus-visible:ring-destructive/50',
                        className,
                    )}
                    aria-invalid={error ? true : undefined}
                    data-cy={fieldDataCy ? `${fieldDataCy}-trigger` : undefined}
                >
                    <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent sideOffset={4}>
                    {options.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                            {option.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <FieldError>{error?.message ? String(error.message) : null}</FieldError>
        </Field>
    );
}

export { RHFSelect };
